import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Printer, RotateCcw } from "lucide-react";
import { devError, devLog } from "@/lib/logger";

const STORAGE_KEY = "tx_cheque_layout_v1";

const DEFAULT_LAYOUT = {
  // mm offsets from top-left of cheque, fine-tunable per bank
  date_x: 145, date_y: 9,
  payee_x: 9, payee_y: 22,
  amount_words_1_x: 9, amount_words_1_y: 32,
  amount_words_2_x: 9, amount_words_2_y: 42,
  amount_box_x: 145, amount_box_y: 42,
  ac_payee: true,             // print "A/C Payee" crossing
};

function useLayout() {
  const [layout, setLayout] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULT_LAYOUT, ...JSON.parse(raw) } : DEFAULT_LAYOUT;
    } catch {
      return DEFAULT_LAYOUT;
    }
  });
  const save = (l) => {
    setLayout(l);
    // Best-effort persistence. If localStorage is full or disabled (private mode),
    // we degrade gracefully — the layout is still applied in the current session.
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(l)); }
    catch (err) { devLog("Cheque layout persist skipped", err); }
  };
  const reset = () => save(DEFAULT_LAYOUT);
  return [layout, save, reset];
}

function Inner() {
  const { vid } = useParams();
  const navigate = useNavigate();
  const [cheque, setCheque] = useState(null);
  const [layout, setLayout, resetLayout] = useLayout();

  useEffect(() => {
    let mounted = true;
    api.get(`/vouchers/${vid}/cheque`).then((r) => { if (mounted) setCheque(r.data); }).catch((err) => devError("Cheque load failed", err));
    return () => { mounted = false; };
  }, [vid]);

  if (!cheque) {
    return <div className="p-6 text-sm text-gray-500" data-testid="cheque-loading">Loading…</div>;
  }

  const update = (k, v) => setLayout({ ...layout, [k]: v });

  // Split amount in words into two physical lines (~52 chars each is plenty)
  const words = cheque.amount_in_words || "";
  let line1 = words, line2 = "";
  if (words.length > 52) {
    const cutoff = words.lastIndexOf(" ", 52);
    line1 = words.slice(0, cutoff);
    line2 = words.slice(cutoff + 1);
  }

  return (
    <>
      <PageHeader
        title={`Print Cheque ${cheque.cheque_no ? `#${cheque.cheque_no}` : ""}`}
        subtitle={`${cheque.bank_name || "—"}  •  ₹ ${formatINR(cheque.amount)} to ${cheque.payee || "—"}`}
        actions={
          <div className="flex items-center gap-2 print:hidden">
            <Button variant="outline" onClick={() => navigate(-1)} className="h-8" data-testid="btn-back-cheques"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
            <Button variant="outline" onClick={resetLayout} className="h-8" data-testid="btn-reset-layout"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
            <Button onClick={() => window.print()} className="h-8" data-testid="btn-print-cheque"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
          </div>
        }
      />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-testid="cheque-print-page">
        {/* Live cheque preview */}
        <div>
          <div className="text-[11px] text-gray-500 mb-2 print:hidden">CTS-2010 cheque — 203 mm × 93 mm. Insert your cheque, align once and save the layout. The preview below is the exact print size.</div>
          <div className="cheque-frame">
            <div className="cheque-canvas" id="cheque-print-area" data-testid="cheque-preview">
              <span
                className="cheque-text"
                style={{ top: `${layout.date_y}mm`, left: `${layout.date_x}mm`, letterSpacing: "1.5mm", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                data-testid="cheque-date"
              >
                {(cheque.date || "").replaceAll("-", "")}
              </span>
              <span
                className="cheque-text"
                style={{ top: `${layout.payee_y}mm`, left: `${layout.payee_x}mm`, fontWeight: 600 }}
                data-testid="cheque-payee"
              >
                {cheque.payee}
              </span>
              <span
                className="cheque-text"
                style={{ top: `${layout.amount_words_1_y}mm`, left: `${layout.amount_words_1_x}mm` }}
                data-testid="cheque-words-1"
              >
                {line1}
              </span>
              {line2 && (
                <span
                  className="cheque-text"
                  style={{ top: `${layout.amount_words_2_y}mm`, left: `${layout.amount_words_2_x}mm` }}
                  data-testid="cheque-words-2"
                >
                  {line2}
                </span>
              )}
              <span
                className="cheque-text"
                style={{ top: `${layout.amount_box_y}mm`, left: `${layout.amount_box_x}mm`, fontWeight: 700 }}
                data-testid="cheque-amount-box"
              >
                ₹ {formatINR(cheque.amount)}
              </span>
              {layout.ac_payee && (
                <span
                  className="cheque-text"
                  style={{ top: "6mm", left: "6mm", transform: "rotate(-12deg)", border: "1px solid #555", padding: "1mm 2mm", fontSize: "3mm", letterSpacing: "0.5mm" }}
                  data-testid="cheque-ac-payee"
                >
                  A/C PAYEE
                </span>
              )}
              {/* Cheque guide outline (visible only on screen — hidden in print) */}
              <div className="cheque-guide print:hidden" />
            </div>
          </div>

          <div className="text-[11px] text-gray-400 mt-3 print:hidden">
            Cheque No.: <span className="font-mono">{cheque.cheque_no || "—"}</span>  •  Voucher: <span className="font-mono">{cheque.voucher_no}</span>  •  Status: <span className="font-mono">{cheque.status}</span>
          </div>
        </div>

        {/* Layout tuner */}
        <aside className="border border-gray-200 rounded bg-white p-4 self-start print:hidden" data-testid="layout-tuner">
          <div className="font-semibold text-gray-900 text-sm mb-3">Layout Offsets (mm)</div>
          <div className="text-[11px] text-gray-500 mb-3">Adjust once per bank, then values are saved in this browser.</div>
          <div className="space-y-3">
            {[
              ["Date", "date"],
              ["Payee", "payee"],
              ["Amount words L1", "amount_words_1"],
              ["Amount words L2", "amount_words_2"],
              ["Amount box", "amount_box"],
            ].map(([label, prefix]) => (
              <div key={prefix} className="grid grid-cols-[100px_1fr_1fr] items-center gap-2">
                <span className="text-xs text-gray-600">{label}</span>
                <Input
                  type="number" step="0.5"
                  value={layout[`${prefix}_x`]}
                  onChange={(e) => update(`${prefix}_x`, Number(e.target.value))}
                  className="h-7 text-xs"
                  data-testid={`input-${prefix}-x`}
                />
                <Input
                  type="number" step="0.5"
                  value={layout[`${prefix}_y`]}
                  onChange={(e) => update(`${prefix}_y`, Number(e.target.value))}
                  className="h-7 text-xs"
                  data-testid={`input-${prefix}-y`}
                />
              </div>
            ))}
            <div className="grid grid-cols-[100px_1fr] items-center gap-2 pt-2 border-t">
              <span className="text-xs text-gray-600">A/C Payee crossing</span>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={layout.ac_payee}
                  onChange={(e) => update("ac_payee", e.target.checked)}
                  data-testid="check-ac-payee"
                />
                Print
              </label>
            </div>
          </div>
          <div className="mt-4 text-[10px] text-gray-400 leading-relaxed">
            Tip: X = distance from left edge of cheque in mm. Y = distance from top. Use the on-screen guide rectangle to align before your first physical print.
          </div>
        </aside>
      </div>

      {/* Print + screen styles */}
      <style>{`
        .cheque-frame {
          background: #f3f4f6;
          border: 1px dashed #d1d5db;
          padding: 8mm;
          width: fit-content;
        }
        .cheque-canvas {
          position: relative;
          width: 203mm;
          height: 93mm;
          background: #ffffff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .cheque-text {
          position: absolute;
          font-size: 4mm;
          color: #0a0a0a;
          white-space: nowrap;
        }
        .cheque-guide {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(to right, rgba(99,102,241,0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(99,102,241,0.06) 1px, transparent 1px);
          background-size: 10mm 10mm;
          pointer-events: none;
        }
        @media print {
          @page { size: 203mm 93mm; margin: 0; }
          body { background: white; }
          body * { visibility: hidden !important; }
          #cheque-print-area, #cheque-print-area * { visibility: visible !important; }
          .cheque-frame {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }
          .cheque-canvas {
            position: fixed !important;
            top: 0; left: 0;
            box-shadow: none !important;
          }
          .cheque-guide { display: none !important; }
        }
      `}</style>
    </>
  );
}

export default function ChequePrint() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
